import { useSelector } from "react-redux";

import { useIntl } from "react-intl-compat";

import { selectAuthAccessToken } from "../../../../components/src/app-main/selectors/auth.selectors";
import { selectProfileSSOid } from "../../../../components/src/app-main/selectors/profile.selectors";
import {
  ListingEditorLayout,
  ListingEditorLayoutSkeleton,
} from "../components/ListingEditorLayout";

import { ListingFsvForm } from "./ListingFsvForm";
import { useListingFsvData } from "./useListingFsvData";

type ListingFsvPageProps = {
  params: { listingId: string };
};

const ListingFsvPage = ({ params: { listingId } }: ListingFsvPageProps) => {
  const accessToken = useSelector(selectAuthAccessToken);
  const ssoId = useSelector(selectProfileSSOid);
  const intl = useIntl();
  const { isError, isFetching, isFsv, listing, space } = useListingFsvData({
    listingId,
  });

  if (!accessToken) {
    return null;
  }

  // The listing or its space couldn't be loaded, so there's nothing to edit
  if (isError) {
    window.location.href = "/mylistings";
    return null;
  }

  const isLoading = isFetching || !listing || !space || !ssoId;

  // Ensure only listing owners can access this page
  if (!isLoading && listing.ownerId !== ssoId) {
    window.location.href = "/mylistings";
    return null;
  }

  // Only FSV listings have this section; send others back to the listing page
  if (!isLoading && !isFsv) {
    window.location.href = `/mylistings/${listingId}`;
    return null;
  }

  return (
    <div
      className="ps-container-lg-margin ps-container-fixed"
      data-testid="fsv-page"
    >
      <div className="ps-row">
        <div className="col-xs-12">
          {isLoading ? (
            <ListingEditorLayoutSkeleton />
          ) : (
            <ListingEditorLayout
              listing={listing}
              space={space}
              testIdPrefix="fsv-page"
              showHeaderDivider
              pageHeader={intl.formatMessage({
                id: "hosting.listing_fsv.page_header",
                defaultMessage: "Pricing details",
              })}
              pageCaption={intl.formatMessage({
                id: "hosting.listing_fsv.page_caption",
                defaultMessage:
                  "Set the starting rates guests see on your listing.",
              })}
            >
              <ListingFsvForm listing={listing} />
            </ListingEditorLayout>
          )}
        </div>
      </div>
    </div>
  );
};

export { ListingFsvPage };
